import { useState, useEffect, useMemo } from "react";
import { Plus, Pencil, Trash2, Image, Upload, X, Save, Bell, Send, ChevronDown, ChevronUp, Zap, Package } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import type { Product, ProductCategory, ProductVariant, ProductImage } from "@/hooks/useProducts";

interface ProductAdminPanelProps {
  products: Product[];
  categories: ProductCategory[];
  onRefresh: () => void;
  onRefreshCategories: () => void;
}

interface CapacityOption {
  gb: number;
  priceAdjustment: number;
}

interface ColorOption {
  name: string;
  hex: string;
  priceAdjustment: number;
}

interface ConditionOption {
  grade: string;
  label: string;
  priceAdjustment: number;
  enabled: boolean;
}

const DEFAULT_CONDITIONS: ConditionOption[] = [
  { grade: "A+", label: "Come Nuovo", priceAdjustment: 0, enabled: true },
  { grade: "A", label: "Eccellente", priceAdjustment: -30, enabled: true },
  { grade: "B", label: "Ottimo", priceAdjustment: -60, enabled: true },
  { grade: "C", label: "Buono", priceAdjustment: -100, enabled: false },
];

const generateSlug = (name: string) => {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
};

export const ProductAdminPanel = ({ products, categories, onRefresh, onRefreshCategories }: ProductAdminPanelProps) => {
  const [showProductDialog, setShowProductDialog] = useState(false);
  const [showCategoryDialog, setShowCategoryDialog] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [editingCategory, setEditingCategory] = useState<ProductCategory | null>(null);
  const [uploading, setUploading] = useState(false);

  // Product form
  const [productForm, setProductForm] = useState({
    name: "",
    description: "",
    category_id: "",
    base_price: "",
    discount_percentage: "0",
    subscription_discount_percentage: "0",
    is_active: true,
  });

  // Swappie-style options
  const [capacities, setCapacities] = useState<CapacityOption[]>([]);
  const [colors, setColors] = useState<ColorOption[]>([]);
  const [conditions, setConditions] = useState<ConditionOption[]>(DEFAULT_CONDITIONS);
  const [newCapacity, setNewCapacity] = useState("");
  const [newColorName, setNewColorName] = useState("");
  const [newColorHex, setNewColorHex] = useState("#000000");

  // Generated variants with editable quantity/battery
  const [generatedVariants, setGeneratedVariants] = useState<Partial<ProductVariant>[]>([]);
  const [images, setImages] = useState<ProductImage[]>([]);
  const [selectedColorForImages, setSelectedColorForImages] = useState<string | null>(null);

  // Sections open state
  const [openSections, setOpenSections] = useState({
    capacities: true,
    colors: true,
    conditions: true,
    variants: true,
    images: true,
  });

  // Category form
  const [categoryForm, setCategoryForm] = useState({ name: "", icon: "smartphone" });

  // Notifications
  const [notificationTitle, setNotificationTitle] = useState("");
  const [notificationBody, setNotificationBody] = useState("");
  const [sendingNotification, setSendingNotification] = useState(false);

  // Extract options from existing variants when editing
  useEffect(() => {
    if (editingProduct) {
      setProductForm({
        name: editingProduct.name,
        description: editingProduct.description || "",
        category_id: editingProduct.category_id || "",
        base_price: editingProduct.base_price.toString(),
        discount_percentage: editingProduct.discount_percentage.toString(),
        subscription_discount_percentage: editingProduct.subscription_discount_percentage.toString(),
        is_active: editingProduct.is_active,
      });
      setImages(editingProduct.images || []);

      // Extract unique capacities, colors, conditions from existing variants
      const existingVariants = editingProduct.variants || [];
      
      const capMap = new Map<number, number>();
      const colMap = new Map<string, string>();
      const condMap = new Map<string, number>();

      existingVariants.forEach(v => {
        if (v.memory_gb && !capMap.has(v.memory_gb)) {
          capMap.set(v.memory_gb, v.price_adjustment || 0);
        }
        if (!colMap.has(v.color)) {
          colMap.set(v.color, v.color_hex || '#000000');
        }
        if (!condMap.has(v.condition_grade)) {
          condMap.set(v.condition_grade, 0);
        }
      });

      // Figure out price adjustments per dimension
      // We need to decompose: variant.price_adjustment = cap_adj + cond_adj
      // Use first color & first condition as baseline to extract capacity adjustments
      const uniqueMemories = [...capMap.keys()].sort((a, b) => a - b);
      const uniqueColors = [...colMap.entries()].map(([name, hex]) => ({ name, hex, priceAdjustment: 0 }));
      const uniqueConditions = [...condMap.keys()];

      // Extract capacity price adjustments (compare variants with same color/condition but different memory)
      const capAdjustments = new Map<number, number>();
      if (uniqueMemories.length > 0) {
        const baseMemory = uniqueMemories[0];
        const baseVariant = existingVariants.find(v => v.memory_gb === baseMemory);
        const baseAdj = baseVariant?.price_adjustment || 0;
        
        uniqueMemories.forEach(mem => {
          const variant = existingVariants.find(v => 
            v.memory_gb === mem && 
            v.color === (baseVariant?.color || uniqueColors[0]?.name) &&
            v.condition_grade === (baseVariant?.condition_grade || uniqueConditions[0])
          );
          capAdjustments.set(mem, (variant?.price_adjustment || 0) - baseAdj);
        });
      }

      setCapacities(uniqueMemories.map(gb => ({
        gb,
        priceAdjustment: capAdjustments.get(gb) || 0,
      })));
      setColors(uniqueColors);
      setConditions(DEFAULT_CONDITIONS.map(c => ({
        ...c,
        enabled: uniqueConditions.includes(c.grade),
      })));
      setGeneratedVariants(existingVariants);
    } else {
      resetProductForm();
    }
  }, [editingProduct]);

  const resetProductForm = () => {
    setProductForm({
      name: "", description: "", category_id: "", base_price: "",
      discount_percentage: "0", subscription_discount_percentage: "0", is_active: true,
    });
    setCapacities([]);
    setColors([]);
    setConditions(DEFAULT_CONDITIONS);
    setGeneratedVariants([]);
    setImages([]);
  };

  // Auto-generate variants from options
  const handleGenerateVariants = () => {
    const enabledConditions = conditions.filter(c => c.enabled);
    if (colors.length === 0) {
      toast.error("Aggiungi almeno un colore");
      return;
    }
    if (enabledConditions.length === 0) {
      toast.error("Seleziona almeno una condizione");
      return;
    }

    const newVariants: Partial<ProductVariant>[] = [];
    const caps = capacities.length > 0 ? capacities : [{ gb: 0, priceAdjustment: 0 }];

    for (const cap of caps) {
      for (const color of colors) {
        for (const cond of enabledConditions) {
          // Check if this variant already exists
          const existing = generatedVariants.find(v =>
            v.memory_gb === (cap.gb || null) &&
            v.color === color.name &&
            v.condition_grade === cond.grade
          );

          newVariants.push({
            id: existing?.id,
            color: color.name,
            color_hex: color.hex,
            memory_gb: cap.gb || null,
            condition_grade: cond.grade,
            battery_percentage: existing?.battery_percentage || 100,
            price_adjustment: cap.priceAdjustment + color.priceAdjustment + cond.priceAdjustment,
            quantity: existing?.quantity || 0,
          });
        }
      }
    }

    setGeneratedVariants(newVariants);
    toast.success(`${newVariants.length} varianti generate`);
  };

  const addCapacity = () => {
    const gb = parseInt(newCapacity);
    if (!gb || capacities.some(c => c.gb === gb)) {
      toast.error("Capacità non valida o già presente");
      return;
    }
    setCapacities([...capacities, { gb, priceAdjustment: 0 }].sort((a, b) => a.gb - b.gb));
    setNewCapacity("");
  };

  const addColor = () => {
    if (!newColorName.trim() || colors.some(c => c.name === newColorName.trim())) {
      toast.error("Nome colore non valido o già presente");
      return;
    }
    setColors([...colors, { name: newColorName.trim(), hex: newColorHex, priceAdjustment: 0 }]);
    setNewColorName("");
    setNewColorHex("#000000");
  };

  const sendPushNotification = async (title: string, body: string, url?: string) => {
    try {
      const { data, error } = await supabase.functions.invoke('send-push-notification', {
        body: { type: 'all', payload: { title, body, url: url || '/prodotti', icon: '/favicon.ico' } }
      });
      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error sending notification:', error);
      throw error;
    }
  };

  const handleUploadImage = async (file: File, colorName: string | null = null) => {
    if (!editingProduct && !productForm.name) {
      toast.error("Salva prima il prodotto per aggiungere immagini");
      return;
    }
    setUploading(true);
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}.${fileExt}`;
      const filePath = `${editingProduct?.id || 'temp'}/${fileName}`;
      const { error: uploadError } = await supabase.storage.from('product-images').upload(filePath, file);
      if (uploadError) throw uploadError;
      const { data: { publicUrl } } = supabase.storage.from('product-images').getPublicUrl(filePath);
      if (editingProduct) {
        const isFirstGlobal = images.filter(img => !img.variant_color).length === 0 && !colorName;
        const { error } = await supabase.from("product_images").insert({
          product_id: editingProduct.id, image_url: publicUrl,
          is_cover: isFirstGlobal, sort_order: images.length,
          variant_color: colorName,
        });
        if (error) throw error;
        const { data: updatedImages } = await supabase.from("product_images").select("*").eq("product_id", editingProduct.id).order("sort_order");
        setImages(updatedImages || []);
      }
      toast.success("Immagine caricata");
    } catch (error) {
      console.error(error);
      toast.error("Errore nel caricamento");
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteImage = async (imageId: string) => {
    try {
      await supabase.from("product_images").delete().eq("id", imageId);
      setImages(images.filter(img => img.id !== imageId));
      toast.success("Immagine eliminata");
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };

  const handleSetCover = async (imageId: string) => {
    if (!editingProduct) return;
    try {
      await supabase.from("product_images").update({ is_cover: false }).eq("product_id", editingProduct.id);
      await supabase.from("product_images").update({ is_cover: true }).eq("id", imageId);
      const coverImg = images.find(img => img.id === imageId);
      if (coverImg) {
        await supabase.from("products").update({ cover_image: coverImg.image_url }).eq("id", editingProduct.id);
      }
      setImages(images.map(img => ({ ...img, is_cover: img.id === imageId })));
      toast.success("Copertina aggiornata");
    } catch (error) {
      toast.error("Errore nell'aggiornamento");
    }
  };

  const handleSaveProduct = async () => {
    if (!productForm.name || !productForm.base_price) {
      toast.error("Nome e prezzo sono obbligatori");
      return;
    }

    try {
      const slug = generateSlug(productForm.name);
      const productData = {
        name: productForm.name, slug,
        description: productForm.description || null,
        category_id: productForm.category_id || null,
        base_price: parseFloat(productForm.base_price),
        discount_percentage: parseFloat(productForm.discount_percentage) || 0,
        subscription_discount_percentage: parseFloat(productForm.subscription_discount_percentage) || 0,
        is_active: productForm.is_active,
        cover_image: images.find(img => img.is_cover)?.image_url || null,
      };

      if (editingProduct) {
        await supabase.from("products").update(productData).eq("id", editingProduct.id);

        // Delete old variants that are no longer in the generated list
        const existingIds = generatedVariants.filter(v => v.id).map(v => v.id!);
        const oldVariants = editingProduct.variants || [];
        const toDelete = oldVariants.filter(v => !existingIds.includes(v.id));
        for (const v of toDelete) {
          await supabase.from("product_variants").delete().eq("id", v.id);
        }

        // Upsert variants
        for (const variant of generatedVariants) {
          if (variant.id) {
            await supabase.from("product_variants").update({
              color: variant.color, color_hex: variant.color_hex || '#000000',
              memory_gb: variant.memory_gb, condition_grade: variant.condition_grade,
              battery_percentage: variant.battery_percentage, price_adjustment: variant.price_adjustment,
              quantity: variant.quantity,
            }).eq("id", variant.id);
          } else {
            await supabase.from("product_variants").insert({
              product_id: editingProduct.id, color: variant.color!,
              color_hex: variant.color_hex || '#000000', memory_gb: variant.memory_gb,
              condition_grade: variant.condition_grade || 'A',
              battery_percentage: variant.battery_percentage || 100,
              price_adjustment: variant.price_adjustment || 0, quantity: variant.quantity || 0,
            });
          }
        }

        const wasDiscountAdded = parseFloat(productForm.discount_percentage) > 0 &&
          editingProduct.discount_percentage === 0 &&
          productForm.name.toLowerCase().includes('iphone');
        if (wasDiscountAdded) {
          try {
            await sendPushNotification(
              `🔥 ${productForm.name} in Sconto!`,
              `Approfitta dello sconto del ${productForm.discount_percentage}% su ${productForm.name}!`
            );
          } catch (e) { console.log('Notification send failed:', e); }
        }
        toast.success("Prodotto aggiornato");
      } else {
        const { data: newProduct, error } = await supabase.from("products").insert(productData).select().single();
        if (error) throw error;

        if (generatedVariants.length > 0) {
          await supabase.from("product_variants").insert(
            generatedVariants.map(v => ({
              product_id: newProduct.id, color: v.color!,
              color_hex: v.color_hex || '#000000', memory_gb: v.memory_gb,
              condition_grade: v.condition_grade || 'A',
              battery_percentage: v.battery_percentage || 100,
              price_adjustment: v.price_adjustment || 0, quantity: v.quantity || 0,
            }))
          );
        }

        try {
          await sendPushNotification(
            `✨ Nuovo: ${productForm.name}`,
            `È appena arrivato ${productForm.name} nel nostro catalogo!`
          );
        } catch (e) { console.log('Notification send failed:', e); }
        toast.success("Prodotto creato");
      }

      setShowProductDialog(false);
      setEditingProduct(null);
      onRefresh();
    } catch (error: any) {
      toast.error(error.message || "Errore nel salvataggio");
    }
  };

  const handleDeleteProduct = async (productId: string) => {
    if (!confirm("Sei sicuro di voler eliminare questo prodotto?")) return;
    try {
      await supabase.from("products").delete().eq("id", productId);
      toast.success("Prodotto eliminato");
      onRefresh();
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };

  const handleSaveCategory = async () => {
    if (!categoryForm.name) { toast.error("Il nome è obbligatorio"); return; }
    try {
      const slug = generateSlug(categoryForm.name);
      if (editingCategory) {
        await supabase.from("product_categories").update({ name: categoryForm.name, slug, icon: categoryForm.icon }).eq("id", editingCategory.id);
        toast.success("Categoria aggiornata");
      } else {
        await supabase.from("product_categories").insert({ name: categoryForm.name, slug, icon: categoryForm.icon });
        toast.success("Categoria creata");
      }
      setShowCategoryDialog(false);
      setEditingCategory(null);
      setCategoryForm({ name: "", icon: "smartphone" });
      onRefreshCategories();
    } catch (error) {
      toast.error("Errore nel salvataggio");
    }
  };

  const handleDeleteCategory = async (categoryId: string) => {
    if (!confirm("Sei sicuro?")) return;
    try {
      await supabase.from("product_categories").delete().eq("id", categoryId);
      toast.success("Categoria eliminata");
      onRefreshCategories();
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };

  const handleSendCustomNotification = async () => {
    if (!notificationTitle.trim() || !notificationBody.trim()) { toast.error("Inserisci titolo e messaggio"); return; }
    setSendingNotification(true);
    try {
      const result = await sendPushNotification(notificationTitle, notificationBody);
      toast.success(`Notifica inviata a ${result.sent} utenti`);
      setNotificationTitle("");
      setNotificationBody("");
    } catch (error) {
      toast.error("Errore nell'invio della notifica");
    } finally {
      setSendingNotification(false);
    }
  };

  const toggleSection = (key: keyof typeof openSections) => {
    setOpenSections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const variantCount = generatedVariants.length;
  const totalStock = generatedVariants.reduce((sum, v) => sum + (v.quantity || 0), 0);

  return (
    <div className="space-y-6">
      <Tabs defaultValue="products">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="products">Prodotti</TabsTrigger>
          <TabsTrigger value="categories">Categorie</TabsTrigger>
          <TabsTrigger value="notifications">Notifiche</TabsTrigger>
        </TabsList>

        <TabsContent value="products" className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Gestione Prodotti</h3>
            <Button onClick={() => { setEditingProduct(null); resetProductForm(); setShowProductDialog(true); }}>
              <Plus className="w-4 h-4 mr-2" /> Nuovo Prodotto
            </Button>
          </div>

          <div className="grid gap-4">
            {products.map(product => (
              <Card key={product.id}>
                <CardContent className="p-4 flex items-center gap-4">
                  <div className="w-16 h-16 bg-muted rounded-lg overflow-hidden flex-shrink-0">
                    {product.cover_image ? (
                      <img src={product.cover_image} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Image className="w-6 h-6 text-muted-foreground" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h4 className="font-medium truncate">{product.name}</h4>
                      {!product.is_active && <Badge variant="secondary">Disattivo</Badge>}
                    </div>
                    <p className="text-sm text-muted-foreground">
                      €{product.base_price} • {product.variants?.length || 0} varianti
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="icon" onClick={() => { setEditingProduct(product); setShowProductDialog(true); }}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button variant="outline" size="icon" onClick={() => handleDeleteProduct(product.id)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
            {products.length === 0 && (
              <p className="text-center text-muted-foreground py-8">Nessun prodotto presente</p>
            )}
          </div>
        </TabsContent>

        <TabsContent value="categories" className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Gestione Categorie</h3>
            <Button onClick={() => { setEditingCategory(null); setCategoryForm({ name: "", icon: "smartphone" }); setShowCategoryDialog(true); }}>
              <Plus className="w-4 h-4 mr-2" /> Nuova Categoria
            </Button>
          </div>
          <div className="grid gap-2">
            {categories.map(category => (
              <Card key={category.id}>
                <CardContent className="p-4 flex items-center justify-between">
                  <div>
                    <h4 className="font-medium">{category.name}</h4>
                    <p className="text-sm text-muted-foreground">{category.slug}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="icon" onClick={() => { setEditingCategory(category); setCategoryForm({ name: category.name, icon: category.icon }); setShowCategoryDialog(true); }}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button variant="outline" size="icon" onClick={() => handleDeleteCategory(category.id)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="notifications" className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Gestione Notifiche</h3>
          </div>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Bell className="w-5 h-5" />Invia Notifica Push</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Titolo *</Label>
                <Input value={notificationTitle} onChange={e => setNotificationTitle(e.target.value)} placeholder="es: Nuovo iPhone disponibile!" />
              </div>
              <div>
                <Label>Messaggio *</Label>
                <Textarea value={notificationBody} onChange={e => setNotificationBody(e.target.value)} placeholder="es: Il nuovo iPhone 15 Pro è finalmente disponibile!" rows={3} />
              </div>
              <Button onClick={handleSendCustomNotification} disabled={sendingNotification || !notificationTitle.trim() || !notificationBody.trim()} className="w-full">
                <Send className="w-4 h-4 mr-2" />{sendingNotification ? "Invio in corso..." : "Invia a tutti gli utenti"}
              </Button>
            </CardContent>
          </Card>
          <Card>
            <CardHeader><CardTitle>Notifiche Automatiche</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                <div><p className="font-medium">Nuovo prodotto</p><p className="text-sm text-muted-foreground">Notifica automatica quando aggiungi un prodotto</p></div>
                <Badge variant="outline" className="text-primary">Attivo</Badge>
              </div>
              <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                <div><p className="font-medium">Prodotto in sconto</p><p className="text-sm text-muted-foreground">Notifica quando un iPhone viene messo in sconto</p></div>
                <Badge variant="outline" className="text-primary">Attivo</Badge>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Product Dialog - Swappie Style */}
      <Dialog open={showProductDialog} onOpenChange={setShowProductDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="w-5 h-5" />
              {editingProduct ? "Modifica Prodotto" : "Nuovo Prodotto"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Section 1: Basic Info */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Informazioni Prodotto</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Nome prodotto *</Label>
                  <Input
                    value={productForm.name}
                    onChange={e => setProductForm({ ...productForm, name: e.target.value })}
                    placeholder="es. iPhone 15 Pro"
                  />
                </div>
                <div>
                  <Label>Descrizione</Label>
                  <Textarea
                    value={productForm.description}
                    onChange={e => setProductForm({ ...productForm, description: e.target.value })}
                    placeholder="Descrizione del prodotto..."
                    rows={2}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Categoria</Label>
                    <Select value={productForm.category_id} onValueChange={v => setProductForm({ ...productForm, category_id: v })}>
                      <SelectTrigger><SelectValue placeholder="Seleziona" /></SelectTrigger>
                      <SelectContent>
                        {categories.map(cat => (
                          <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Prezzo base (€) *</Label>
                    <Input type="number" value={productForm.base_price} onChange={e => setProductForm({ ...productForm, base_price: e.target.value })} placeholder="699" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Sconto standard (%)</Label>
                    <Input type="number" value={productForm.discount_percentage} onChange={e => setProductForm({ ...productForm, discount_percentage: e.target.value })} placeholder="0" />
                  </div>
                  <div>
                    <Label>Sconto abbonati (%)</Label>
                    <Input type="number" value={productForm.subscription_discount_percentage} onChange={e => setProductForm({ ...productForm, subscription_discount_percentage: e.target.value })} placeholder="0" />
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Switch checked={productForm.is_active} onCheckedChange={v => setProductForm({ ...productForm, is_active: v })} />
                  <Label>Prodotto attivo</Label>
                </div>
              </CardContent>
            </Card>

            {/* Section 2: Capacities */}
            <Collapsible open={openSections.capacities} onOpenChange={() => toggleSection('capacities')}>
              <Card>
                <CollapsibleTrigger asChild>
                  <CardHeader className="cursor-pointer hover:bg-muted/30 transition-colors pb-3">
                    <CardTitle className="text-base flex items-center justify-between">
                      <span>📦 Capacità</span>
                      <div className="flex items-center gap-2">
                        {capacities.length > 0 && <Badge variant="secondary">{capacities.length}</Badge>}
                        {openSections.capacities ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </div>
                    </CardTitle>
                  </CardHeader>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <CardContent className="space-y-3 pt-0">
                    <div className="flex gap-2">
                      <Input type="number" value={newCapacity} onChange={e => setNewCapacity(e.target.value)} placeholder="es. 256" className="w-28"
                        onKeyDown={e => e.key === 'Enter' && addCapacity()} />
                      <span className="flex items-center text-sm text-muted-foreground">GB</span>
                      <Button variant="outline" size="sm" onClick={addCapacity}><Plus className="w-4 h-4" /></Button>
                    </div>
                    {capacities.length > 0 && (
                      <div className="space-y-2">
                        {capacities.map((cap, i) => (
                          <div key={cap.gb} className="flex items-center gap-3 p-2 bg-muted/30 rounded-lg">
                            <Badge variant="outline" className="font-mono">{cap.gb} GB</Badge>
                            <div className="flex items-center gap-1 flex-1">
                              <span className="text-xs text-muted-foreground">Sovrapprezzo:</span>
                              <Input
                                type="number"
                                value={cap.priceAdjustment}
                                onChange={e => {
                                  const updated = [...capacities];
                                  updated[i] = { ...updated[i], priceAdjustment: parseFloat(e.target.value) || 0 };
                                  setCapacities(updated);
                                }}
                                className="w-24 h-8 text-sm"
                                placeholder="0"
                              />
                              <span className="text-xs text-muted-foreground">€</span>
                            </div>
                            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setCapacities(capacities.filter((_, j) => j !== i))}>
                              <X className="w-3 h-3" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                    {capacities.length === 0 && (
                      <p className="text-xs text-muted-foreground">Nessuna capacità — opzionale per accessori</p>
                    )}
                  </CardContent>
                </CollapsibleContent>
              </Card>
            </Collapsible>

            {/* Section 3: Colors */}
            <Collapsible open={openSections.colors} onOpenChange={() => toggleSection('colors')}>
              <Card>
                <CollapsibleTrigger asChild>
                  <CardHeader className="cursor-pointer hover:bg-muted/30 transition-colors pb-3">
                    <CardTitle className="text-base flex items-center justify-between">
                      <span>🎨 Colori</span>
                      <div className="flex items-center gap-2">
                        {colors.length > 0 && <Badge variant="secondary">{colors.length}</Badge>}
                        {openSections.colors ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </div>
                    </CardTitle>
                  </CardHeader>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <CardContent className="space-y-3 pt-0">
                    <div className="flex gap-2 items-end">
                      <div className="flex-1">
                        <Input value={newColorName} onChange={e => setNewColorName(e.target.value)} placeholder="es. Titanio Nero"
                          onKeyDown={e => e.key === 'Enter' && addColor()} />
                      </div>
                      <input type="color" value={newColorHex} onChange={e => setNewColorHex(e.target.value)} className="w-10 h-10 rounded-lg border border-input cursor-pointer" />
                      <Button variant="outline" size="sm" onClick={addColor}><Plus className="w-4 h-4" /></Button>
                    </div>
                    {colors.length > 0 && (
                      <div className="space-y-2">
                        {colors.map((color, i) => (
                          <div key={color.name} className="flex items-center gap-3 p-2 bg-muted/30 rounded-lg">
                            <span className="w-4 h-4 rounded-full border border-border flex-shrink-0" style={{ backgroundColor: color.hex }} />
                            <span className="text-sm flex-1">{color.name}</span>
                            <div className="flex items-center gap-1">
                              <span className="text-xs text-muted-foreground">Sovrapprezzo:</span>
                              <Input
                                type="number"
                                value={color.priceAdjustment}
                                onChange={e => {
                                  const updated = [...colors];
                                  updated[i] = { ...updated[i], priceAdjustment: parseFloat(e.target.value) || 0 };
                                  setColors(updated);
                                }}
                                className="w-24 h-8 text-sm"
                                placeholder="0"
                              />
                              <span className="text-xs text-muted-foreground">€</span>
                            </div>
                            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setColors(colors.filter((_, j) => j !== i))}>
                              <X className="w-3 h-3" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </CollapsibleContent>
              </Card>
            </Collapsible>

            {/* Section 4: Conditions */}
            <Collapsible open={openSections.conditions} onOpenChange={() => toggleSection('conditions')}>
              <Card>
                <CollapsibleTrigger asChild>
                  <CardHeader className="cursor-pointer hover:bg-muted/30 transition-colors pb-3">
                    <CardTitle className="text-base flex items-center justify-between">
                      <span>✅ Condizioni</span>
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary">{conditions.filter(c => c.enabled).length}</Badge>
                        {openSections.conditions ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </div>
                    </CardTitle>
                  </CardHeader>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <CardContent className="space-y-2 pt-0">
                    {conditions.map((cond, i) => (
                      <div key={cond.grade} className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${cond.enabled ? 'bg-primary/5 border-primary/20' : 'bg-muted/20 border-border opacity-60'}`}>
                        <Switch
                          checked={cond.enabled}
                          onCheckedChange={v => {
                            const updated = [...conditions];
                            updated[i] = { ...updated[i], enabled: v };
                            setConditions(updated);
                          }}
                        />
                        <div className="flex-1">
                          <span className="font-medium text-sm">Grado {cond.grade}</span>
                          <span className="text-xs text-muted-foreground ml-2">({cond.label})</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Input
                            type="number"
                            value={cond.priceAdjustment}
                            onChange={e => {
                              const updated = [...conditions];
                              updated[i] = { ...updated[i], priceAdjustment: parseFloat(e.target.value) || 0 };
                              setConditions(updated);
                            }}
                            className="w-20 h-8 text-sm"
                            disabled={!cond.enabled}
                          />
                          <span className="text-xs text-muted-foreground">€</span>
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </CollapsibleContent>
              </Card>
            </Collapsible>

            {/* Generate Button */}
            <Button onClick={handleGenerateVariants} className="w-full gap-2" variant="default" size="lg">
              <Zap className="w-5 h-5" />
              Genera Varianti ({(capacities.length || 1) * colors.length * conditions.filter(c => c.enabled).length} combinazioni)
            </Button>

            {/* Section 5: Generated Variants */}
            {generatedVariants.length > 0 && (
              <Collapsible open={openSections.variants} onOpenChange={() => toggleSection('variants')}>
                <Card>
                  <CollapsibleTrigger asChild>
                    <CardHeader className="cursor-pointer hover:bg-muted/30 transition-colors pb-3">
                      <CardTitle className="text-base flex items-center justify-between">
                        <span>📋 Varianti Generate</span>
                        <div className="flex items-center gap-2">
                          <Badge variant="secondary">{variantCount} varianti</Badge>
                          <Badge variant="outline">{totalStock} in stock</Badge>
                          {openSections.variants ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </div>
                      </CardTitle>
                    </CardHeader>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <CardContent className="pt-0">
                      <div className="space-y-2 max-h-64 overflow-y-auto">
                        {generatedVariants.map((variant, i) => (
                          <div key={i} className="flex items-center gap-2 p-2 bg-muted/20 rounded-lg text-sm">
                            <span className="w-3 h-3 rounded-full flex-shrink-0 border border-border" style={{ backgroundColor: variant.color_hex || '#000' }} />
                            <span className="flex-1 truncate">
                              {variant.color}
                              {variant.memory_gb ? ` · ${variant.memory_gb}GB` : ''}
                              {` · Grado ${variant.condition_grade}`}
                            </span>
                            <span className="text-xs text-muted-foreground whitespace-nowrap">
                              {(variant.price_adjustment || 0) >= 0 ? '+' : ''}{variant.price_adjustment || 0}€
                            </span>
                            <div className="flex items-center gap-1">
                              <Label className="text-xs text-muted-foreground">Qtà:</Label>
                              <Input
                                type="number"
                                value={variant.quantity || 0}
                                onChange={e => {
                                  const updated = [...generatedVariants];
                                  updated[i] = { ...updated[i], quantity: parseInt(e.target.value) || 0 };
                                  setGeneratedVariants(updated);
                                }}
                                className="w-16 h-7 text-xs"
                              />
                            </div>
                            <div className="flex items-center gap-1">
                              <Label className="text-xs text-muted-foreground">Batt:</Label>
                              <Input
                                type="number"
                                value={variant.battery_percentage || 100}
                                onChange={e => {
                                  const updated = [...generatedVariants];
                                  updated[i] = { ...updated[i], battery_percentage: parseInt(e.target.value) || 100 };
                                  setGeneratedVariants(updated);
                                }}
                                className="w-16 h-7 text-xs"
                              />
                              <span className="text-xs">%</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </CollapsibleContent>
                </Card>
              </Collapsible>
            )}

            {/* Section 6: Images */}
            <Collapsible open={openSections.images} onOpenChange={() => toggleSection('images')}>
              <Card>
                <CollapsibleTrigger asChild>
                  <CardHeader className="cursor-pointer hover:bg-muted/30 transition-colors pb-3">
                    <CardTitle className="text-base flex items-center justify-between">
                      <span>🖼️ Immagini</span>
                      <div className="flex items-center gap-2">
                        {images.length > 0 && <Badge variant="secondary">{images.length}</Badge>}
                        {openSections.images ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </div>
                    </CardTitle>
                  </CardHeader>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <CardContent>
                    {!editingProduct ? (
                      <p className="text-center text-muted-foreground py-4 text-sm">Salva prima il prodotto per aggiungere immagini</p>
                    ) : (
                      <div className="space-y-5">
                        {/* Color filter tabs for images */}
                        <div className="flex flex-wrap gap-2">
                          <button
                            onClick={() => setSelectedColorForImages(null)}
                            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                              selectedColorForImages === null
                                ? 'border-primary bg-primary text-primary-foreground'
                                : 'border-border bg-background text-foreground hover:border-primary/50'
                            }`}
                          >
                            🖼️ Generale
                          </button>
                          {colors.map(color => (
                            <button
                              key={color.name}
                              onClick={() => setSelectedColorForImages(color.name)}
                              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all flex items-center gap-1.5 ${
                                selectedColorForImages === color.name
                                  ? 'border-primary bg-primary text-primary-foreground'
                                  : 'border-border bg-background text-foreground hover:border-primary/50'
                              }`}
                            >
                              <span className="w-3 h-3 rounded-full border border-current/30 flex-shrink-0" style={{ backgroundColor: color.hex }} />
                              {color.name}
                            </button>
                          ))}
                        </div>

                        {/* Upload button */}
                        <div className="flex justify-center">
                          <label className="cursor-pointer">
                            <input type="file" accept="image/*" className="hidden"
                              onChange={e => e.target.files?.[0] && handleUploadImage(e.target.files[0], selectedColorForImages)}
                              disabled={uploading} />
                            <Button variant="outline" disabled={uploading} asChild>
                              <span>
                                <Upload className="w-4 h-4 mr-2" />
                                {uploading ? "Caricamento..." : selectedColorForImages ? `Carica per ${selectedColorForImages}` : "Carica immagine generale"}
                              </span>
                            </Button>
                          </label>
                        </div>

                        {/* Filtered images grid */}
                        {(() => {
                          const filteredImgs = images.filter(img =>
                            selectedColorForImages === null
                              ? !img.variant_color
                              : img.variant_color === selectedColorForImages
                          );
                          return filteredImgs.length > 0 ? (
                            <div className="grid grid-cols-3 gap-4">
                              {filteredImgs.map(img => (
                                <div key={img.id} className="relative group">
                                  <div className={`aspect-square rounded-lg overflow-hidden border-2 ${img.is_cover ? 'border-primary' : 'border-border'}`}>
                                    <img src={img.image_url} alt="" className="w-full h-full object-cover" />
                                  </div>
                                  {img.is_cover && <Badge className="absolute top-2 left-2">Copertina</Badge>}
                                  {img.variant_color && (
                                    <Badge variant="outline" className="absolute bottom-2 left-2 text-[10px]">{img.variant_color}</Badge>
                                  )}
                                  <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                    {!img.is_cover && !img.variant_color && (
                                      <Button size="icon" variant="secondary" className="h-7 w-7" onClick={() => handleSetCover(img.id)}>
                                        <Image className="w-3 h-3" />
                                      </Button>
                                    )}
                                    <Button size="icon" variant="destructive" className="h-7 w-7" onClick={() => handleDeleteImage(img.id)}>
                                      <X className="w-3 h-3" />
                                    </Button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-center text-muted-foreground py-4 text-sm">
                              {selectedColorForImages
                                ? `Nessuna immagine per "${selectedColorForImages}" — verranno usate le immagini generali`
                                : "Nessuna immagine generale caricata"}
                            </p>
                          );
                        })()}
                      </div>
                    )}
                  </CardContent>
                </CollapsibleContent>
              </Card>
            </Collapsible>

            {/* Save */}
            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button variant="outline" onClick={() => setShowProductDialog(false)}>Annulla</Button>
              <Button onClick={handleSaveProduct}><Save className="w-4 h-4 mr-2" /> Salva Prodotto</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Category Dialog */}
      <Dialog open={showCategoryDialog} onOpenChange={setShowCategoryDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingCategory ? "Modifica Categoria" : "Nuova Categoria"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nome categoria *</Label>
              <Input value={categoryForm.name} onChange={e => setCategoryForm({ ...categoryForm, name: e.target.value })} placeholder="iPhone" />
            </div>
            <div>
              <Label>Icona</Label>
              <Select value={categoryForm.icon} onValueChange={v => setCategoryForm({ ...categoryForm, icon: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="smartphone">Smartphone</SelectItem>
                  <SelectItem value="laptop">Laptop</SelectItem>
                  <SelectItem value="tablet">Tablet</SelectItem>
                  <SelectItem value="watch">Watch</SelectItem>
                  <SelectItem value="headphones">Headphones</SelectItem>
                  <SelectItem value="package">Package</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={() => setShowCategoryDialog(false)}>Annulla</Button>
            <Button onClick={handleSaveCategory}><Save className="w-4 h-4 mr-2" /> Salva</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
